import type {
  OnboardingAnswers,
  OnboardingAudience,
  OnboardingBusinessRole,
  OnboardingStepName,
  OnboardingUserType,
} from "./types";

/**
 * localStorage keys for this flow.
 *
 * Deliberately separate from the legacy carousel's `onboardingCompletedAtom`
 * (`@/store/atoms`): this onboarding runs once for *every* user, including
 * everyone who already dismissed the old "Before you use Sociability" slider.
 *
 * The step is persisted as well as the answers — unlike native. The docked
 * sheet (`16150:269296`) follows the visitor from page to page until they are
 * done with it, so a reload or a new tab has to pick up where they left off
 * rather than put them back on step 1.
 */
export const ONBOARDING_COMPLETED_KEY = "onboardingV2Completed";
export const ONBOARDING_ANSWERS_KEY = "onboardingV2Answers";
export const ONBOARDING_STEP_KEY = "onboardingV2Step";
/** Whether the docked sheet is collapsed to its progress pill. */
export const ONBOARDING_MINIMISED_KEY = "onboardingV2Minimised";
/**
 * Set when a brand-new account signs up and still owes us the flow - @see
 * `onboardingV2PendingNewUserAtom`.
 *
 * Persisted, because the thing it has to survive is exactly a reload: someone
 * who closes the tab half way through their first run would otherwise never be
 * offered it again, the device's completion flag having been set long ago by
 * whoever used this browser first.
 */
export const ONBOARDING_PENDING_NEW_USER_KEY = "onboardingV2PendingNewUser";
/**
 * How far this browser got through the intro that comes before the questions
 * - @see `onboardingV2IntroStageAtom`.
 */
export const ONBOARDING_INTRO_STAGE_KEY = "onboardingV2IntroStage";

/**
 * How long the intro waits before it opens. The frames' note (`16204:290230`):
 * "Load the page in the background first, and show the modal after 1500ms" —
 * the visitor sees where they landed before a cookie-banner style modal
 * covers it.
 */
export const ONBOARDING_INTRO_DELAY_MS = 1500;

/**
 * One slide of "What you can do" (`16156:290154`), the carousel home's
 * "Contribute to help the community" cards open.
 */
export type OnboardingWhatYouCanDoSlide = {
  icon: "camera" | "chat" | "warning" | "bell";
  titleKey: string;
  bodyKey: string;
  /** The "App only" badge: the feature can only be used in the mobile app. */
  isAppOnly: boolean;
  /** The picture comes from the CMS album, by position; this describes it. */
  imageAltKey: string;
};

export const ONBOARDING_WHAT_YOU_CAN_DO: OnboardingWhatYouCanDoSlide[] = [
  {
    icon: "camera",
    titleKey: "onboardingWhatYouCanDo1Title",
    bodyKey: "onboardingWhatYouCanDo1Body",
    isAppOnly: true,
    imageAltKey: "onboardingWhatYouCanDo1ImageAlt",
  },
  {
    icon: "chat",
    titleKey: "onboardingWhatYouCanDo2Title",
    bodyKey: "onboardingWhatYouCanDo2Body",
    isAppOnly: false,
    imageAltKey: "onboardingWhatYouCanDo2ImageAlt",
  },
  {
    icon: "warning",
    titleKey: "onboardingWhatYouCanDo3Title",
    bodyKey: "onboardingWhatYouCanDo3Body",
    isAppOnly: true,
    imageAltKey: "onboardingWhatYouCanDo3ImageAlt",
  },
  {
    icon: "bell",
    titleKey: "onboardingWhatYouCanDo4Title",
    bodyKey: "onboardingWhatYouCanDo4Body",
    isAppOnly: false,
    imageAltKey: "onboardingWhatYouCanDo4ImageAlt",
  },
];

/**
 * One page of "Show me how it works" (`16204:290230`, the six split modals).
 * The copy and the picture's description are i18n keys; the picture is the
 * page's row in the "Show me around" albums, matched by position.
 */
export type OnboardingIntroTourPage = {
  /** The picture comes from the CMS album, by position; this describes it. */
  imageAltKey: string;
  /**
   * The pin above the heading: the orange "Sociability verified" pin while the
   * pages talk about places Sociability has visited, the grey one once they
   * turn to places that still need information.
   */
  pin: "verified" | "unverified";
  titleKey: string;
  bodyKey: string;
};

export const ONBOARDING_INTRO_TOUR: OnboardingIntroTourPage[] = [
  {
    imageAltKey: "onboardingIntroTour1ImageAlt",
    pin: "verified",
    titleKey: "onboardingIntroTour1Title",
    bodyKey: "onboardingIntroTour1Body",
  },
  {
    imageAltKey: "onboardingIntroTour2ImageAlt",
    pin: "verified",
    titleKey: "onboardingIntroTour2Title",
    bodyKey: "onboardingIntroTour2Body",
  },
  {
    imageAltKey: "onboardingIntroTour3ImageAlt",
    pin: "verified",
    titleKey: "onboardingIntroTour3Title",
    bodyKey: "onboardingIntroTour3Body",
  },
  {
    imageAltKey: "onboardingIntroTour4ImageAlt",
    pin: "unverified",
    titleKey: "onboardingIntroTour4Title",
    bodyKey: "onboardingIntroTour4Body",
  },
  {
    imageAltKey: "onboardingIntroTour5ImageAlt",
    pin: "unverified",
    titleKey: "onboardingIntroTour5Title",
    bodyKey: "onboardingIntroTour5Body",
  },
  {
    imageAltKey: "onboardingIntroTour6ImageAlt",
    pin: "unverified",
    titleKey: "onboardingIntroTour6Title",
    bodyKey: "onboardingIntroTour6Body",
  },
];

/**
 * Routes where the docked sheet starts collapsed to its pill, matched against
 * the **landing** path of the session rather than the current one.
 *
 * Inherited from the carousel this replaces
 * (`OnboardingScreensComponent.shouldOpenOnboardingModal`), which kept out of
 * these entirely: someone who followed a link to a venue, a password reset or a
 * verification link came for that page, and a 603px sheet over it is still in
 * the way. The pill is not, so they get the pill. Campaign traffic likewise.
 * Once the visitor opens or collapses the sheet themselves, their choice wins.
 */
export const ONBOARDING_EXCLUDED_ROUTES = [
  "/passwordreset",
  "/verifyuser",
  "/venuedetails",
];

export const ONBOARDING_EXCLUDED_QUERY_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_term",
];

/**
 * One path through the flow. Step 1 forks on native, so the order of steps is a
 * function of the answers rather than a single flat list — kept that shape here
 * so the business branch drops in without restructuring.
 */
export type OnboardingSequence = {
  /** Every step on this path, in order. */
  steps: OnboardingStepName[];
  /**
   * The subset that renders "Step N of M", in order. The intro and the claim
   * confirmation are part of the flow but aren't steps the user works through,
   * so neither is counted.
   */
  countedSteps: OnboardingStepName[];
  /**
   * The M in "Step N of M". Stated rather than derived from `countedSteps` so a
   * branch can advertise its real length while some of its screens are still
   * being built — otherwise every screen that lands renumbers the ones before
   * it, and a user part-way through sees the total jump.
   */
  totalCountedSteps: number;
};

/**
 * The individual path, for a visitor who is **signed in**.
 *
 * `WELCOME` is omitted rather than listed-and-skipped: the CMS intro has no
 * desktop screen, and a step in `steps` that isn't in `IMPLEMENTED_STEPS` would
 * make `goNext` a no-op at the point it was reached.
 *
 * **Four counted steps, not the three the desktop frame draws.** `15984:127545`
 * reads "Step 1 of 3", which predates native splitting the old single "Why are
 * you here?" question into "Hi there! 👋 Let's get started" and "Who are you
 * here for?" — @see `apps/native/src/onboarding/constants.ts`, where the same
 * note lives. Desktop mirrors the native flow, so it mirrors its numbering.
 */
export const ONBOARDING_SEQUENCE_DEFAULT: OnboardingSequence = {
  steps: ["USER_TYPE", "AUDIENCE", "ACCESS_NEEDS", "CITY"],
  countedSteps: ["USER_TYPE", "AUDIENCE", "ACCESS_NEEDS", "CITY"],
  totalCountedSteps: 4,
};

/**
 * The individual path for a **signed-out** visitor: the default one with "How
 * did you hear about us?" before the city, as the overlay frames draw it
 * ("Step 4 of 5", `16150:269296`).
 *
 * Signed-out only, because the answer has one destination: the sign-up
 * payload (`useSignUpProfilePayload`). A visitor who answers here and signs up
 * later is not asked again - sign-up skips answered questions. A signed-in
 * account has either answered it at sign-up already or, if it predates this
 * flow, has no endpoint to send it to, so asking would collect an answer that
 * goes nowhere.
 *
 * Then "Let’s jump in!", uncounted: someone with no account is offered one
 * once the city is picked. Signed in, there is nothing to offer, so the
 * default path ends on the city.
 */
export const ONBOARDING_SEQUENCE_SIGNED_OUT: OnboardingSequence = {
  steps: [
    "USER_TYPE",
    "AUDIENCE",
    "ACCESS_NEEDS",
    "HEAR_ABOUT",
    "CITY",
    "JUMP_IN",
  ],
  countedSteps: ["USER_TYPE", "AUDIENCE", "ACCESS_NEEDS", "HEAR_ABOUT", "CITY"],
  totalCountedSteps: 5,
};

/**
 * The questions asked inside the auth modal right after an account is created
 * (`16150:279245`, "Create an account without completing onboarding
 * questions"), before the email is verified.
 *
 * The individual path's first three questions, then "How did you hear about
 * us?" in place of the city — the frames count four steps and ask no city.
 * The city is still owed afterwards, so the full modal asks for it after the
 * first sign-in, carrying on from these answers.
 */
export const ONBOARDING_SIGN_UP_STEPS: OnboardingStepName[] = [
  "USER_TYPE",
  "AUDIENCE",
  "ACCESS_NEEDS",
  "HEAR_ABOUT",
];

/** Whether `step` already has an answer on record. */
const isStepAnswered = (
  step: OnboardingStepName,
  answers: OnboardingAnswers,
): boolean => {
  switch (step) {
    case "USER_TYPE":
      return !!answers.userType;
    case "AUDIENCE":
      return !!answers.audience;
    case "ACCESS_NEEDS":
      return answers.accessNeedsAnswered;
    case "CITY":
    case "BUSINESS_CITY":
      return !!answers.city;
    case "HEAR_ABOUT":
      return !!answers.hearAbout;
    // The confirmation has nothing to answer: it is done once the claim it
    // confirms has been filed.
    case "BUSINESS_CLAIM":
    case "CLAIM_SUBMITTED":
      return !!answers.businessClaim;
    default:
      return false;
  }
};

/**
 * The first step in `steps` still waiting for an answer, or `undefined` when
 * all of them have one. How one sequence picks up where the other left off:
 * sign-up skips what the docked sheet already asked, and the modal after the
 * first sign-in opens on what sign-up did not ask.
 */
export const firstUnansweredStep = (
  steps: OnboardingStepName[],
  answers: OnboardingAnswers,
): OnboardingStepName | undefined =>
  steps.find((step) => !isStepAnswered(step, answers));

/**
 * The business branch: "Business use" on step 1, then the claim form, its
 * confirmation and "Let’s explore!" — native's order
 * (`apps/native/src/onboarding/constants.ts`).
 *
 * One path whether signed in or not. "How did you hear about us?" is left out
 * even for a signed-out visitor: the city screen follows the confirmation
 * directly, and picking the city is what ends the flow, so there is nowhere
 * after it to ask. That makes the claim form "Step 2 of 3", where the docked
 * frame (`16224:292764`) reads "Step 2 of 4".
 *
 * The confirmation is part of the path but not counted: it is not a question.
 */
export const ONBOARDING_SEQUENCE_BUSINESS: OnboardingSequence = {
  steps: ["USER_TYPE", "BUSINESS_CLAIM", "CLAIM_SUBMITTED", "BUSINESS_CITY"],
  countedSteps: ["USER_TYPE", "BUSINESS_CLAIM", "BUSINESS_CITY"],
  totalCountedSteps: 3,
};

/**
 * Steps with a screen behind them. The flow refuses to navigate to anything
 * missing from this list, so half-built branches are inert rather than dead
 * ends — add a step here the moment its screen lands.
 */
export const IMPLEMENTED_STEPS: OnboardingStepName[] = [
  "USER_TYPE",
  "AUDIENCE",
  "ACCESS_NEEDS",
  "HEAR_ABOUT",
  "CITY",
  "JUMP_IN",
  "BUSINESS_CLAIM",
  "CLAIM_SUBMITTED",
  "BUSINESS_CITY",
];

const isBranchImplemented = (sequence: OnboardingSequence) =>
  sequence.steps.every((step) => IMPLEMENTED_STEPS.includes(step));

/**
 * Which path the answers so far, and whether anyone is signed in, put the user
 * on.
 *
 * The business answer only diverts while the whole branch exists — a guard
 * kept so that taking a step out of `IMPLEMENTED_STEPS` falls back to the
 * default path rather than stranding the user.
 */
export const sequenceForAnswers = (
  answers: OnboardingAnswers,
  { isSignedIn }: { isSignedIn: boolean },
): OnboardingSequence => {
  if (
    answers.userType === "business" &&
    isBranchImplemented(ONBOARDING_SEQUENCE_BUSINESS)
  ) {
    return ONBOARDING_SEQUENCE_BUSINESS;
  }
  return isSignedIn
    ? ONBOARDING_SEQUENCE_DEFAULT
    : ONBOARDING_SEQUENCE_SIGNED_OUT;
};

/** A card on one of the two single-choice steps, in the order Figma draws it. */
export type OnboardingChoiceOption<T extends string> = {
  value: T;
  /** i18n key for the card's title. */
  labelKey: string;
  /** i18n key for the sentence under it. */
  descriptionKey: string;
};

/** Step 1 — "Hi there! 👋 Let's get started" (`15984:127545`). */
export const ONBOARDING_USER_TYPE_OPTIONS: OnboardingChoiceOption<OnboardingUserType>[] =
  [
    {
      value: "individual",
      labelKey: "onboardingUserTypePersonal",
      descriptionKey: "onboardingUserTypePersonalDescription",
    },
    {
      value: "business",
      labelKey: "onboardingUserTypeBusiness",
      descriptionKey: "onboardingUserTypeBusinessDescription",
    },
  ];

/** "Your role in the business" chips, in the order Figma draws them. */
export const ONBOARDING_BUSINESS_ROLES: {
  value: OnboardingBusinessRole;
  labelKey: string;
}[] = [
  { value: "owner", labelKey: "onboardingBusinessRoleOwner" },
  { value: "general_manager", labelKey: "onboardingBusinessRoleGeneralManager" },
  { value: "others", labelKey: "onboardingBusinessRoleOthers" },
];

/** Step 2 — "Who are you here for?". */
export const ONBOARDING_AUDIENCE_OPTIONS: OnboardingChoiceOption<OnboardingAudience>[] =
  [
    {
      value: "myself",
      labelKey: "onboardingAudienceMyself",
      descriptionKey: "onboardingAudienceMyselfDescription",
    },
    {
      value: "someone_i_care_for",
      labelKey: "onboardingAudienceSomeoneICareFor",
      descriptionKey: "onboardingAudienceSomeoneICareForDescription",
    },
  ];
